import COLORS from 'constants/colors';
import { defaultsDeep } from 'lodash';

import {
  getSeedRateBinsForScript,
  getSeedRateColorFromBins
} from 'util/formatters/data-viz/seed';
import {
  getSeedApplicationsForZone
} from 'scripts/zones/planting-selectors';
import {
  getFertilizerRateBinsForScript,
  getFertilizerRateColorFromBins
} from 'util/formatters/data-viz/fertilizer';
import { zoneIsSelected, zoneIsDisabled } from 'util/zoneDrawing';
import { convertForLocaleScalar } from 'util/unitConversion';
import { fertilityProductFormats } from 'constants/products';
import {
  getApplicationTypeForScriptType,
  getScriptTypeForScript
} from 'scripts/selectors';
import { getApplicationsForZoneByType } from 'scripts/zones/selectors';
import { getFormatFromZones } from 'products/selectors';
import { getColorFromRelativeProductivity } from 'manual/util/zone-generation';
import { getAxisBins, getAxisBounds } from 'util/formatters/data-viz/axis';
import { ADVANCED_SEED_BIN_ROUNDING_VALUE, NUM_SEED_RATE_BINS } from 'constants/data-viz';

export const aggregateMapConfig = (config) => {
  return defaultsDeep({}, config, {
    baseConfig: {
      width: 300,
      height: 300
    },
    mapConfig: {
      padding: 0,
      fillMethod: () => '#cccccc',
      stroke: '#ffffff',
      strokeWidth: 1
    }
  });
};

export const aggregateThumbnailConfig = (config) => {
  return defaultsDeep({}, config, {
    baseConfig: {
      width: 50,
      height: 50
    },
    mapConfig: {
      width: 50,
      height: 50,
      stroke: '#fafafa',
      strokeWidth: 0.5
    }
  });
};

export const aggregateManagementZoneThumbnailConfig = (config) => {
  return defaultsDeep({}, config, {
    baseConfig: {
      width: 175,
      height: 175
    },
    mapConfig: {
      width: 175,
      height: 175,
      fillMethod: () => ({
        fillColor: COLORS.managementZones.fill
      }),
      stroke: COLORS.managementZones.boundary
    }
  });
};

export const styleMethods = {
  editingZones: ({ drawingState, selectedZoneIds }, zone) => {
    const isSelected = zoneIsSelected(selectedZoneIds,zone);
    const isDisabled = zoneIsDisabled(drawingState, zone);
    // TODO: Figure out how to handle the below
    // const defaultStrokeColor = ComparisonLayerStore.getSelectedBorderColor();
    const sharedProps = {
      weight: 2,
      opacity: 1,
      fillOpacity: isSelected ? 0.3 : 0,
      fillColor: '#ffffff'
    };

    return isDisabled ? {...sharedProps,
      fillColor: '#cccccc',
      fillOpacity: 1
    } : {...sharedProps,
      color: '#ffffff',
      weight: isSelected ? 4 : 2
    };

  },
  fertilizerRate: (script, productValidationValues, zone) => {
    const applicationType = getApplicationTypeForScriptType(getScriptTypeForScript(script));
    const bins = getFertilizerRateBinsForScript(script, productValidationValues);

    // TODO: refactor this line CFVWEB-8862
    let rate = { rate: { q: null, u: 'gal/ac' }};
    if(getApplicationsForZoneByType({ zone, applicationType })[0]) {
      rate = getApplicationsForZoneByType({ zone, applicationType })[0].rate;
    }

    // Need to get rate relating to user's input, not value in state
    const format = getFormatFromZones([zone]);
    const unitType = format === fertilityProductFormats.WET
      ? 'volumetric-density' : 'area-density.application.dry-fertilizer';
    const convertedRate = convertForLocaleScalar({ quantity: rate.q, unit: rate.u, unitType });

    return {
      fillColor: getFertilizerRateColorFromBins({ bins, rate: convertedRate }),
      fillOpacity: 0.6,
      color: '#ffffff',
      weight: 1
    };
  },
  filled: (color) => {
    return {
      fillColor: color,
      color: '#ffffff',
      weight: 2,
      fillOpacity: 0.3
    };
  },
  outline: () => {
    return {
      color: COLORS.null,
      weight: 2,
      fillOpacity: 0
    };
  },
  generatedZones: (rowHover, zone, zones) => {
    const zoneIndex = String(zone.meta.index);

    let fillColor = '#00000000';
    let borderWeight = '2';

    if (zones) {
      fillColor = getColorFromRelativeProductivity({
        zones: Object.values(zones),
        relativeProductivity: zone?.geoJson?.properties?.relativeProductivity?.v
      });
    }

    if (rowHover) {
      borderWeight = '0';
      if (zones) fillColor = '#00000000';

      if (rowHover === zoneIndex) {
        fillColor = '#F6F6F6';
        borderWeight = '2';
      }
    }

    return {
      fillColor,
      fillOpacity: 1,
      color: '#FFFFFF',
      weight: borderWeight
    };
  },
  seedRate: (script, productValidationValues, zone) => {
    const bins = getSeedRateBinsForScript(script, productValidationValues);
    const { rate } = getSeedApplicationsForZone(zone)[0];

    // Need to get rate relating to user's input, not value in state
    const convertedRate = convertForLocaleScalar({ quantity: rate.q, unit: rate.u, unitType: 'seed-density' });
    return {
      fillColor: getSeedRateColorFromBins({ bins, rate: convertedRate }),
      fillOpacity: 0.6,
      color: '#ffffff',
      weight: 1
    };
  },
  seedRateForBulkReview: (bins, zone) => {
    const rate = zone.seedingRate;
    const convertedRate = convertForLocaleScalar({ quantity: rate.v, unit: rate.u, unitType: 'seed-density' });

    return {
      fillColor: getSeedRateColorFromBins({ bins, rate: convertedRate }),
      fillOpacity: 0.6,
      color: '#ffffff',
      weight: 1
    };
  },
  soil: (zone) => {
    return {
      fillColor: COLORS.soil[zone.meta.index] || null,
      fillOpacity: 0.6,
      weight: 0
    };
  },
  trialPlacements: zoneTypesById => zone => {
    const zoneId = zone.id;
    if(zoneTypesById.champion === zoneId) {
      return {
        fillColor: '#FFFFFF',
        color: '#FFFFFF',
        weight: 2,
        fillOpacity: 0.7
      };
    }

    if(zoneTypesById.challenger === zoneId) {
      return {
        fillColor: '#FFFFFF',
        weight: 0,
        fillOpacity: 0.3
      };
    }

    return {
      color: COLORS.null,
      weight: 1,
      fillOpacity: 0
    };

  },
  zoneHighlight: (zoneId, zone) => {
    return {
      fillColor: zone.meta.zoneId === zoneId ? '#c2c2c2' : '#dbdbdb'
    };
  }
};
